import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Upload, Copy, Trash2, Loader2, Image as ImageIcon, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ABS = (path) => (path?.startsWith("http") ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`);

export default function MediaLibrary({ token }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState("");
  const fileRef = useRef(null);

  const reload = async () => {
    try {
      const r = await axios.get(`${API}/admin/media`, { headers: { "X-Admin-Token": token } });
      setItems(r.data || []);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const upload = async (files) => {
    if (!files?.length) return;
    setError(""); setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("alt", file.name);
        await axios.post(`${API}/admin/media/upload`, fd, {
          headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
        });
      }
      await reload();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const copyUrl = async (item) => {
    const url = ABS(item.url || `/api/media/${item.id}`);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(""), 1600);
    } catch { /* ignore */ }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API}/admin/media/${id}`, { headers: { "X-Admin-Token": token } });
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const fmtKB = (b) => (b / 1024 < 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <section className="bg-white rounded-2xl border border-cream-200 overflow-hidden" data-testid="media-library">
      <div className="p-5 border-b border-cream-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-xl text-navy-deep">Media Library</h3>
          <p className="text-sm text-navy/55 mt-1">Upload images from your computer. Use the URL anywhere on the site — devotionals, pages, banners.</p>
        </div>
        <div className="flex items-center gap-3">
          {uploading && <span className="text-sm text-navy/60 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Uploading…</span>}
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
          <button data-testid="media-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="btn-gold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50">
            <Upload size={16} /> Upload Images
          </button>
        </div>
      </div>

      {error && <div className="m-5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">{error}</div>}

      <div className="p-5">
        {items.length === 0 ? (
          <div className="border-2 border-dashed border-cream-200 rounded-2xl p-12 text-center"
               onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
               onDragOver={(e) => e.preventDefault()}>
            <ImageIcon size={36} className="text-navy/30 mx-auto mb-3" />
            <p className="text-navy/60">No images uploaded yet.</p>
            <p className="text-xs text-navy/45 mt-1">Drag images here, or click <strong>Upload Images</strong> above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
               onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
               onDragOver={(e) => e.preventDefault()}>
            {items.map((m) => {
              const url = ABS(m.url || `/api/media/${m.id}`);
              return (
                <div key={m.id} data-testid={`media-card-${m.id}`}
                  className="rounded-xl bg-cream-50 border border-cream-200 overflow-hidden flex flex-col">
                  <a href={url} target="_blank" rel="noreferrer" className="aspect-square bg-white block">
                    <img src={url} alt={m.alt} className="w-full h-full object-cover" loading="lazy" />
                  </a>
                  <div className="p-3 flex-1 flex flex-col">
                    <p className="text-xs font-medium text-navy truncate" title={m.filename}>{m.filename}</p>
                    <p className="text-[0.65rem] text-navy/50 mt-0.5">{fmtKB(m.size)} · {fmtDate(m.uploaded_at)}</p>
                    <div className="mt-3 flex gap-2">
                      <button data-testid={`media-copy-${m.id}`} onClick={() => copyUrl(m)}
                        className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
                          copiedId === m.id ? "bg-green-100 text-green-800" : "bg-white border border-cream-200 hover:border-gold text-navy"
                        }`}>
                        {copiedId === m.id ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy URL</>}
                      </button>
                      <button data-testid={`media-delete-${m.id}`} onClick={() => del(m.id)}
                        aria-label="Delete" className="px-2.5 py-1.5 rounded-full bg-white border border-cream-200 hover:border-red-300 hover:text-red-600 text-navy/60">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
